<template>
  <div class="container py-5">

    <div class="buttons pb-3">
      <a
        @click="addingSupplier = true"
        class="button is-primary">
        Add a Supplier
      </a>
      <a class="button"
        v-if="selected.length > 0"
        @click="launchConfirmDelete(true)">
        <span class="icon is-medium">
          <i class="fas fa-trash has-text-danger"></i>
        </span>
        <span>Delete {{ selected.length }} Selected Suppliers</span>
      </a>
    </div>

    <article 
      v-if="!loading && suppliers.length == 0" 
      class="message is-info is-fullwidth">
      <div class="message-body">
        There are no suppliers.
      </div>
    </article>

    <table v-else class="table is-fullwidth is-hoverable">

      <thead>
        <tr>
          <th 
            v-for="(header, headerIndex) in headers"
            :key="headerIndex">
            {{ header }}
          </th>
          <th></th>
        </tr>
      </thead>

      <tbody>
        <tr 
          class="v-centered"
          v-for="supplier in suppliers"
          :key="supplier.id">

          <!-- Not in edit mode -->
          <template v-if="supplier.id != editingId">
            <td>
              {{ supplier.name }}
            </td>
            <td>
              {{ supplier.desc }}
            </td>
            <td>
              {{ supplier.address }}
            </td>
            <td>
              {{ supplier.city }}
            </td>
            <td>
              {{ supplier.province }}
            </td>
            <td>
              {{ supplier.country }}
            </td>
            <td>
              {{ supplier.postalcode }}
            </td>
            <td>
              {{ supplier.phone1 }}
            </td>
          </template>

          <!-- Edit mode -->
          <template v-else>
            <td>
              <v-text-field 
                required
                v-model="supplier.name"
                @blur="update(supplier.id, 'name', $event)"/> 
            </td>
            <td>
              <v-text-field 
                required
                v-model="supplier.desc"
                @blur="update(supplier.id, 'desc', $event)"/> 
            </td>
            <td>
              <v-text-field 
                required
                v-model="supplier.address"
                @blur="update(supplier.id, 'address', $event)"/> 
            </td>
            <td>
              <v-text-field 
                required
                v-model="supplier.city"
                @blur="update(supplier.id, 'city', $event)"/> 
            </td>
            <td>
              <v-text-field 
                required
                v-model="supplier.province"
                @blur="update(supplier.id, 'province', $event)"/> 
            </td>
            <td>
              <v-text-field 
                required
                v-model="supplier.country"
                @blur="update(supplier.id, 'country', $event)"/> 
            </td>
            <td>
              <v-text-field 
                required
                v-model="supplier.postalcode"
                @blur="update(supplier.id, 'postalcode', $event)"/> 
            </td>
            <td>
              <v-text-field 
                required
                v-model="supplier.phone1"
                @blur="update(supplier.id, 'phone1', $event)"/> 
            </td>
          </template>

          <td>
            <div class="buttons buttons-right">
              <template v-if="supplier.id != editingId">
                <button 
                  @click="editingId = supplier.id"
                  class="button is-small has-text-grey">
                  <span class="icon">
                    <i class="fas fa-edit"></i>
                  </span>
                  <span>Edit</span>
                </button>
                <button 
                  @click="deleteId = supplier.id; launchConfirmDelete()"
                  class="button is-small has-text-grey">
                  <span class="icon">
                    <i class="fas fa-trash"></i>
                  </span>
                  <span>Delete</span>
                </button>
              </template>
              
              <template v-else>
                <button 
                  @click="editingId = null"
                  class="button is-small is-primary">
                  <span class="icon">
                    <i class="fas fa-check"></i>
                  </span>
                  <span>Done Edits</span>
                </button>
              </template>
            </div>
          </td>

        </tr>
      </tbody>

    </table>

    <v-pagination 
      v-if="pagination.pages > 1"
      :value="pagination.page"
      @input="changePage"
      :length="pagination.pages"/>

    <add-supplier-modal
      v-model="addingSupplier"/>

    <v-confirm-modal 
      v-model="confirmDelete.state"
      :title="confirmDelete.title"
      :message="confirmDelete.message"
      :type="confirmDelete.type"
      @confirm="deleteConfirmed()"
      @cancel="deleteId = null; confirmDelete.state = false"/>

  </div>
</template>

<script>
import VTextField from '@/components/base/VTextField'
import VConfirmModal from '@/components/base/VConfirmModal'
import VPagination from '@/components/base/VPagination'
import AddSupplierModal from '@/components/inventory/AddSupplierModal'

import { mapState, mapActions, mapMutations } from 'vuex'

export default {
  components: { 
    VTextField,
    VConfirmModal, 
    VPagination, 
    AddSupplierModal 
  },
  data() {
    return {
      headers: [
        'Name', 'Description', 'Address', 'City', 'Province/State',
        'Country', 'Postal Code', 'Phone'
      ],
      confirmDelete: {
        state: false,
        title: 'Delete Confirmation',
        message: 'Are you sure you want to delete this supplier?',
        type: 'danger'
      },
      editingId: null,
      deleteId: null,
      bulk: false,
      addingSupplier: false
    }
  },
  created() {
    this.initialize();
  },
  computed: {
    ...mapState("suppliers", [
      "suppliers", "pagination", "selected", "loading"
    ])
  },
  watch: {
    'pagination.page'() {
      this.getSuppliers(this.pagination);
    }
  },
  methods: {
    ...mapActions("suppliers", [
      "getSuppliers", "deleteSupplier", "updateSupplier"
    ]),
    ...mapMutations("suppliers", ["changePage"]),
    initialize() {
      if (this.suppliers.length == 0) {
        this.getSuppliers();
      }
    },
    launchConfirmDelete(bulk=false) {
      this.bulk = bulk;
      if (bulk) {
        this.confirmDelete.message = `Are you sure you want to delete the ${this.selected.length} suppliers?`;
      } else {
        this.confirmDelete.message = 'Are you sure you want to delete this supplier?'
      }
      this.confirmDelete.state = true;
    },
    deleteConfirmed() {
      this.confirmDelete.state = false;

      if (this.bulk == true) {
        this.deleteSelected();
      } else {
        this.deleteSupplier(this.deleteId).then(
          () => this.deleteId = null
        );
      }
    },
    update(id, field, v) {
      let data = {};
      data[field] = v;

      this.updateSupplier({ supplierId: id, data: data });
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/table.scss';
</style>